import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Translate, ArrowCounterClockwise, Check } from "@phosphor-icons/react";
import { AdminShell } from "@/components/AdminShell";
import api, { errDetail } from "@/lib/api";

const LANGS = ["en", "es", "pt", "fr"];
const LABEL = { en: "English", es: "Español", pt: "Português", fr: "Français" };

export default function AdminLanguages() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // {key, lang}
  const [val, setVal] = useState("");

  const load = () => api.get("/admin/i18n").then((r) => setData(r.data)).catch((e) => toast.error(errDetail(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/admin/i18n", { lang: editing.lang, key: editing.key, value: val });
      toast.success("Translation saved — live for users now");
      setEditing(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const reset = async (key, lang) => {
    try {
      await api.delete(`/admin/i18n?lang=${lang}&key=${encodeURIComponent(key)}`);
      toast.success("Reset to default");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const rows = (data?.rows || []).filter((r) => {
    if (q && !`${r.key} ${r.en} ${r.es} ${r.pt} ${r.fr}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (filter === "missing") return LANGS.some((l) => !r[l]);
    if (filter === "overridden") return (r.overridden || []).length > 0;
    if (filter === "backend") return !r.key.startsWith("nav.") && !r.key.startsWith("landing.") && !r.key.startsWith("agent.") && !r.key.startsWith("wh.") && !(r.key.startsWith("brief.") && ["brief.title", "brief.send_at", "brief.preview_btn", "brief.building"].includes(r.key));
    return true;
  });

  return (
    <AdminShell>
      <div data-testid="admin-languages-page">
        <div className="flex items-center gap-2 mb-1">
          <Translate size={22} className="text-blue-600" />
          <h1 className="font-heading text-xl font-bold text-gray-900">System Languages</h1>
        </div>
        <p className="text-sm text-gray-500 mb-4">Every system string in English with its Spanish, Portuguese and French translations. Edits go live for users instantly. The Super Admin panel itself always stays in English.</p>
        {data && (
          <p data-testid="i18n-coverage" className="text-xs text-gray-500 mb-3">
            {data.total} keys{LANGS.slice(1).map((l) => ` · ${l} ${Math.round((data.stats[l] / Math.max(1, data.total)) * 100)}%`).join("")}
          </p>
        )}
        <div className="flex flex-wrap gap-2 mb-3">
          <input data-testid="i18n-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search key or text…"
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-64" />
          {["all", "missing", "overridden", "backend"].map((f) => (
            <button key={f} data-testid={`i18n-filter-${f}`} onClick={() => setFilter(f)}
              className={`text-xs font-semibold rounded-full border px-3 py-1.5 capitalize transition-colors ${filter === f ? "bg-blue-50 border-blue-300 text-blue-700" : "border-gray-200 text-gray-500"}`}>{f}</button>
          ))}
        </div>
        <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
          <table className="w-full text-xs" data-testid="i18n-table">
            <thead><tr className="border-b border-gray-200 text-left text-gray-500">
              <th className="p-2 font-semibold">Key</th>
              {LANGS.map((l) => <th key={l} className="p-2 font-semibold">{LABEL[l]}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} data-testid={`i18n-row-${r.key}`} className="border-b border-gray-100 align-top">
                  <td className="p-2 font-mono text-gray-500 whitespace-nowrap">{r.key}</td>
                  <td className="p-2 text-gray-800 max-w-56">{r.en}</td>
                  {LANGS.slice(1).map((l) => (
                    <td key={l} className="p-2 max-w-56">
                      {editing && editing.key === r.key && editing.lang === l ? (
                        <span className="flex items-center gap-1">
                          <input data-testid="i18n-edit-input" autoFocus value={val} onChange={(e) => setVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(null); }}
                            className="border border-blue-300 rounded px-1.5 py-1 w-full" />
                          <button data-testid="i18n-edit-save" onClick={save} className="text-green-600 p-0.5"><Check size={14} /></button>
                        </span>
                      ) : (
                        <span className="flex items-start gap-1 group">
                          <button data-testid={`i18n-cell-${r.key}-${l}`} onClick={() => { setEditing({ key: r.key, lang: l }); setVal(r[l] || ""); }}
                            className={`text-left flex-1 hover:bg-blue-50 rounded px-1 ${r[l] ? "text-gray-800" : "text-red-400 italic"}`}>
                            {r[l] || "missing"}
                          </button>
                          {(r.overridden || []).includes(l) && (
                            <button data-testid={`i18n-reset-${r.key}-${l}`} title="Reset to default" onClick={() => reset(r.key, l)}
                              className="text-amber-500 hover:text-amber-700 p-0.5"><ArrowCounterClockwise size={12} /></button>
                          )}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <p className="p-4 text-sm text-gray-400">No keys match.</p>}
        </div>
      </div>
    </AdminShell>
  );
}
